import './index.scss';
import {useColorScheme} from "@mui/material/styles";
import React, {FunctionComponent, useContext, useEffect, useMemo, useState} from "react";
import {Id, toast, ToastContainer as ToastifyToastContainer, ToastContainerProps as ToastifyToastContainerProps} from "react-toastify";
import {useUpdateEffect} from "react-use";
import {Utils} from '../../../core';

export interface ToastContainerContextValue {
    activeToastIds: Array<Id>;
}

const ToastContainerContext = React.createContext<ToastContainerContextValue>({
    activeToastIds: [],
});

/**
 * A hook that returns the active toast ids.
 * @return {*[]}
 */
export const useToastContainer = (): ToastContainerContextValue => {
    return useContext(ToastContainerContext);
}

export interface ToastContainerProps extends ToastifyToastContainerProps {
}


const ToastContainer: FunctionComponent<ToastContainerProps> = (props) => {
    const {mode, systemMode} = useColorScheme();
    const [toastIds, setToastIds] = useState<Array<Id>>([]);

    const containerId = useMemo(() => props.containerId ?? Utils.createUUId(), [props.containerId]);

    const toastTheme: 'light' | 'dark' = useMemo(() => {
        switch (mode) {
            case "dark":
            case "light":
                return mode;
            default:
                switch (systemMode) {
                    case "dark":
                    case "light":
                        return systemMode;
                    default:
                        return 'light';
                }
        }
    }, [mode, systemMode])

    /**
     * As soon as the component mounts:
     * - attaches an event listener on the toasts that would store the ids of the added toasts and remove the ids of the removed toasts.
     */
    useEffect(() => toast.onChange(e => {
        if (e.containerId !== containerId)
            return;
        switch (e.status) {
            case "added":
                setToastIds(prevState => [...prevState, e.id]);
                break;
            case "removed":
                setToastIds(prevState => prevState.filter(id => id !== e.id))
                break;
            default:
                break;
        }
    }), [containerId])

    /**
     * With each change in the [toastTheme] memo value:
     * - updates the theme of the shown toasts to be in sync the current [toastTheme].
     */
    useUpdateEffect(() => {
        setToastIds(prevState => {
            for (let toastId of prevState) {
                if (toast.isActive(toastId))
                    toast.update(toastId, {
                        theme: toastTheme,
                    });
            }
            return prevState;
        })
    }, [toastTheme])

    const containerProviderValue = useMemo<ToastContainerContextValue>(() => ({
        activeToastIds: toastIds,
    }), [toastIds])

    return (
        <>
            <ToastContainerContext.Provider value={containerProviderValue}>
                <ToastifyToastContainer
                    containerId={containerId}
                    theme={toastTheme}
                    stacked
                    closeButton={false}
                    {...props}
                />
            </ToastContainerContext.Provider>
        </>
    );
}

export default ToastContainer;
